import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Login = () => {
  const { login, register: registerUser, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const allowRegister = process.env.REACT_APP_ALLOW_PUBLIC_REGISTER === 'true';

  // Redirect to flights if already logged in
  useEffect(() => {
    if (user) {
      navigate('/flights', { replace: true });
    }
  }, [user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const result = isRegister
        ? await registerUser(data)
        : await login(data.email, data.password);
      if (result.success) {
        // Navigate to flights page after successful login/register
        navigate('/flights', { replace: true });
      }
      // Erro já tratado no contexto
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    if (!allowRegister) return;
    setIsRegister(!isRegister);
    reset();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img 
          src="/drone-consult-logo.svg" 
          alt="Drone Consult Watermark" 
          className="w-full h-full object-cover opacity-5"
        />
      </div>
      <div className="max-w-md w-full space-y-8 relative z-10">
        <div>
          <div className="mx-auto mb-4">
            <img 
              src="/drone-consult-logo.svg" 
              alt="Drone Consult Logo" 
              className="mx-auto h-24 w-auto object-contain"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isRegister ? 'Criar conta' : 'Entrar na sua conta'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            PulverControl - Sistema de Registro de Voos
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {isRegister && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Nome completo
                </label>
                <input
                  {...register('name', { 
                    required: 'Nome é obrigatório',
                    minLength: { value: 2, message: 'Nome deve ter pelo menos 2 caracteres' }
                  })}
                  type="text"
                  className={`mt-1 input ${errors.name ? 'input-error' : ''}`}
                  placeholder="Seu nome completo"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-danger-600">{errors.name.message}</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                {...register('email', {
                  required: 'Email é obrigatório',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido'
                  }
                })}
                type="email"
                className={`mt-1 input ${errors.email ? 'input-error' : ''}`}
                placeholder="seu@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Senha
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('password', {
                    required: 'Senha é obrigatória',
                    minLength: { value: 8, message: 'Senha deve ter pelo menos 8 caracteres' }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
                  placeholder="Sua senha"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-danger-600">{errors.password.message}</p>
              )}
            </div>

            {isRegister && (
              <div>
                <label htmlFor="license_number" className="block text-sm font-medium text-gray-700">
                  Número da licença (opcional)
                </label>
                <input
                  {...register('license_number')}
                  type="text"
                  className="mt-1 input"
                  placeholder="Número da sua licença"
                />
              </div>
            )}

            {isRegister && (
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Telefone (opcional)
                </label>
                <input
                  {...register('phone')}
                  type="tel"
                  className="mt-1 input"
                  placeholder="(11) 99999-9999"
                />
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                isRegister ? 'Criar conta' : 'Entrar'
              )}
            </button>
          </div>

          {allowRegister ? (
            <div className="text-center">
              <button
                type="button"
                onClick={toggleMode}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                {isRegister 
                  ? 'Já tem uma conta? Faça login' 
                  : 'Não tem uma conta? Cadastre-se'
                }
              </button>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-600">
              Seu administrador fornece o login e a senha.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
